<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, type CSSProperties } from 'vue'

import { softwareStages } from '../../data/siteContent'
import {
  createLifecycleTrackPath,
  findNearestLifecycleStage,
  getLifecycleStageFractions,
  getLifecycleStageIndex,
  type TrackPoint,
} from '../../lib/lifecycleTrack'

defineProps<{
  reducedMotion: boolean
}>()

const visual = ref<HTMLElement | null>(null)
const trackSvg = ref<SVGSVGElement | null>(null)
const trackPath = ref<SVGPathElement | null>(null)
const activeIndex = ref(0)
const pathData = ref('')
const pathLength = ref(1)
const stagePoints = ref<TrackPoint[]>([])
const trackWidth = ref(1)
const trackHeight = ref(1)

const stageFractions = getLifecycleStageFractions(softwareStages.length)
const activeStage = computed(() => softwareStages[activeIndex.value]!)
const activePoint = computed(() => stagePoints.value[activeIndex.value] ?? { x: 0, y: 0 })
const activeLength = computed(() => pathLength.value * stageFractions[activeIndex.value]!)
const platformPoint = computed(() => ({
  x:
    trackWidth.value < 620
      ? trackWidth.value * 0.69
      : activePoint.value.x * 0.34 + trackWidth.value * 0.43,
  y: trackHeight.value * (trackWidth.value < 620 ? 0.93 : 0.82),
}))
const visualStyle = computed(
  () =>
    ({
      '--active-x': `${(activePoint.value.x / trackWidth.value) * 100}%`,
      '--active-y': `${(activePoint.value.y / trackHeight.value) * 100}%`,
      '--platform-focus-x': `${(platformPoint.value.x / trackWidth.value) * 100}%`,
    }) as CSSProperties,
)
const readoutStyle = computed(
  () =>
    ({
      '--readout-link-y': `${(activePoint.value.y / trackHeight.value) * 100}%`,
    }) as CSSProperties,
)

let resizeObserver: ResizeObserver | undefined
let visibilityObserver: IntersectionObserver | undefined
let manualSelectionUntil = 0
let scrubPointerId: number | undefined
let trackVisible = false

function sampleTrack() {
  const path =
    trackPath.value ?? visual.value?.querySelector<SVGPathElement>('[data-lifecycle-track]') ?? null
  if (!path) return

  const totalLength = Math.max(path.getTotalLength(), 1)
  pathLength.value = totalLength
  stagePoints.value = stageFractions.map((fraction) => {
    const point = path.getPointAtLength(totalLength * fraction)
    return { x: point.x, y: point.y }
  })
}

async function rebuildTrack() {
  const element = visual.value
  if (!element) return

  trackWidth.value = Math.max(element.clientWidth, 1)
  trackHeight.value = Math.max(element.clientHeight, 1)
  pathData.value = createLifecycleTrackPath(trackWidth.value, trackHeight.value)
  await nextTick()
  sampleTrack()
}

function setActiveStage(index: number, manual = false) {
  activeIndex.value = Math.min(softwareStages.length - 1, Math.max(0, index))
  if (manual) manualSelectionUntil = performance.now() + 900
}

function handleLifecycleProgress(event: Event) {
  if (performance.now() < manualSelectionUntil) return
  const progress = (event as CustomEvent<number>).detail
  if (!Number.isFinite(progress)) return
  setActiveStage(getLifecycleStageIndex(progress, softwareStages.length))
}

function setAnimationsPaused(paused: boolean) {
  const svg = trackSvg.value
  if (!svg) return
  if (paused) svg.pauseAnimations()
  else svg.unpauseAnimations()
}

function handleVisibility() {
  setAnimationsPaused(document.hidden || !trackVisible)
}

function updateFromScrub(event: PointerEvent) {
  const element = visual.value
  if (!element || scrubPointerId !== event.pointerId) return
  const bounds = element.getBoundingClientRect()
  const point = { x: event.clientX - bounds.left, y: event.clientY - bounds.top }
  setActiveStage(findNearestLifecycleStage(point, stagePoints.value), true)
}

function startScrub(event: PointerEvent) {
  if (event.pointerType === 'touch') return
  if (event.target instanceof Element && event.target.closest('button')) return
  scrubPointerId = event.pointerId
  visual.value?.setPointerCapture(event.pointerId)
  updateFromScrub(event)
}

function stopScrub(event: PointerEvent) {
  if (scrubPointerId !== event.pointerId) return
  if (visual.value?.hasPointerCapture(event.pointerId)) {
    visual.value.releasePointerCapture(event.pointerId)
  }
  scrubPointerId = undefined
}

onMounted(() => {
  void rebuildTrack()
  resizeObserver = new ResizeObserver(() => void rebuildTrack())
  if (visual.value) resizeObserver.observe(visual.value)

  visibilityObserver = new IntersectionObserver(
    ([entry]) => {
      trackVisible = entry?.isIntersecting ?? false
      setAnimationsPaused(!trackVisible || document.hidden)
    },
    { rootMargin: '100px' },
  )
  if (visual.value) visibilityObserver.observe(visual.value)

  window.addEventListener('se:lifecycle-progress', handleLifecycleProgress)
  document.addEventListener('visibilitychange', handleVisibility)
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  visibilityObserver?.disconnect()
  window.removeEventListener('se:lifecycle-progress', handleLifecycleProgress)
  document.removeEventListener('visibilitychange', handleVisibility)
})
</script>

<template>
  <section id="lifecycle" class="section lifecycle-section" aria-labelledby="lifecycle-title">
    <div class="lifecycle-section__tunnel" aria-hidden="true">
      <i v-for="index in 7" :key="index" :style="{ '--index': index }" />
    </div>
    <div class="section-frame">
      <div class="section-heading" data-reveal>
        <p class="section-kicker">01 / FROM IDEA TO REALITY</p>
        <h2 id="lifecycle-title">软件如何诞生</h2>
        <p>软件工程不是只写代码，而是让一个想法可靠地运行起来。</p>
      </div>

      <div class="lifecycle-console" data-reveal>
        <div
          ref="visual"
          class="lifecycle-console__visual"
          :style="visualStyle"
          data-pointer-depth="lifecycle"
          @pointerdown="startScrub"
          @pointermove="updateFromScrub"
          @pointerup="stopScrub"
          @pointercancel="stopScrub"
        >
          <svg
            ref="trackSvg"
            class="lifecycle-track"
            :viewBox="`0 0 ${trackWidth} ${trackHeight}`"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="lifecycle-progress-gradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0" stop-color="#2187ff" />
                <stop offset="0.58" stop-color="#32e6ff" />
                <stop offset="1" stop-color="#d7fbff" />
              </linearGradient>
              <filter id="lifecycle-track-glow" x="-30%" y="-80%" width="160%" height="260%">
                <feGaussianBlur stdDeviation="3.5" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <path class="lifecycle-track__shadow" :d="pathData" />
            <path
              class="lifecycle-line"
              :d="pathData"
              data-lifecycle-track
              vector-effect="non-scaling-stroke"
            />
            <path
              class="lifecycle-progress"
              :d="pathData"
              :stroke-dasharray="`${activeLength} ${Math.max(pathLength - activeLength, 0.01)}`"
              :data-stage="activeIndex"
              data-lifecycle-progress
              vector-effect="non-scaling-stroke"
            />
            <path
              class="lifecycle-flow"
              :d="pathData"
              :pathLength="pathLength"
              vector-effect="non-scaling-stroke"
            />
            <line
              class="lifecycle-projection"
              :x1="activePoint.x"
              :y1="activePoint.y"
              :x2="platformPoint.x"
              :y2="platformPoint.y"
              vector-effect="non-scaling-stroke"
            />
            <g v-if="!reducedMotion">
              <circle
                v-for="signal in 2"
                :key="signal"
                class="lifecycle-signal"
                :r="signal === 1 ? 4 : 2.5"
              >
                <animateMotion
                  :path="pathData"
                  :dur="signal === 1 ? '5.2s' : '7.4s'"
                  :begin="signal === 1 ? '0s' : '-3.1s'"
                  repeatCount="indefinite"
                />
              </circle>
            </g>
            <circle
              class="lifecycle-projection__target"
              :cx="platformPoint.x"
              :cy="platformPoint.y"
              r="4"
            />
          </svg>

          <button
            v-for="(stage, index) in softwareStages"
            :key="stage.id"
            class="lifecycle-node"
            :class="{ active: index === activeIndex, passed: index < activeIndex }"
            :style="{
              left: `${stagePoints[index]?.x ?? 0}px`,
              top: `${stagePoints[index]?.y ?? 0}px`,
              '--stage-progress': stageFractions[index],
              '--signal-delay': `${-stageFractions[index]! * 5.2}s`,
            }"
            type="button"
            :aria-pressed="index === activeIndex"
            :aria-label="`${stage.label}：${stage.description}`"
            @click="setActiveStage(index, true)"
          >
            <span>{{ String(index + 1).padStart(2, '0') }}</span>
            <strong>{{ stage.label }}</strong>
            <i aria-hidden="true" />
          </button>

          <div class="lifecycle-system" aria-hidden="true">
            <span v-for="index in 9" :key="index" :style="{ '--index': index }" />
            <i class="lifecycle-system__focus" />
            <strong>LIVE<br />SYSTEM</strong>
          </div>
        </div>

        <div
          class="lifecycle-console__readout"
          :style="readoutStyle"
          aria-live="polite"
          aria-atomic="true"
        >
          <span class="lifecycle-readout__link" aria-hidden="true" />
          <Transition name="lifecycle-readout" mode="out-in">
            <div :key="activeStage.id" class="lifecycle-readout__content">
              <p>{{ String(activeIndex + 1).padStart(2, '0') }} / 08 · {{ activeStage.english }}</p>
              <h3>{{ activeStage.label }}</h3>
              <span>{{ activeStage.description }}</span>
              <dl>
                <dt>阶段产物</dt>
                <dd>{{ activeStage.output }}</dd>
              </dl>
            </div>
          </Transition>
        </div>
      </div>

      <p class="lifecycle-section__note" data-reveal>
        每次上线不是终点。反馈会沿着轨道返回，成为下一轮迭代的起点。
      </p>
    </div>
  </section>
</template>
